import {
  codeToFlag,
  COUNTRIES,
  getCountryFlag,
} from "@/src/constants/countries";
import { useTheme } from "@/src/styles/theme-provider";
import { ArrowDown2, CloseCircle, SearchNormal1 } from "iconsax-react-nativejs";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FlatList, Modal, Pressable, Text, TextInput, View } from "react-native";

type CountryPickerProps = Readonly<{
  value: string;
  canEdit: boolean;
  onChange: (value: string) => void;
}>;

const CountryPicker = ({ value, canEdit, onChange }: CountryPickerProps) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const flag = getCountryFlag(value);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return COUNTRIES;
    return COUNTRIES.filter((country) =>
      country.name.toLowerCase().includes(term),
    );
  }, [search]);

  const close = () => {
    setOpen(false);
    setSearch("");
  };

  const select = (name: string) => {
    onChange(name);
    close();
  };

  return (
    <View className="flex-1 gap-1">
      <Text className="text-base text-onyx">{t("LABEL_COUNTRY")}</Text>
      <View className="flex-row items-center md:border md:border-ash-3 bg-ash rounded-[10px] px-3 h-[50px] gap-2">
        {canEdit ? (
          <Pressable
            onPress={() => setOpen(true)}
            className="flex-1 flex-row items-center justify-between"
          >
            <Text className="text-slate text-base flex-1" numberOfLines={1}>
              {value ? `${flag ? `${flag}  ` : ""}${value}` : t("LABEL_SELECT_COUNTRY")}
            </Text>
            <ArrowDown2 size={18} color={themeVars["--color-ash-2"] as string} />
          </Pressable>
        ) : (
          <Text className="text-slate text-base flex-1" numberOfLines={1}>
            {value ? `${flag ? `${flag}  ` : ""}${value}` : t("LABEL_SELECT_COUNTRY")}
          </Text>
        )}
      </View>

      <Modal
        visible={open}
        transparent
        animationType="fade"
        onRequestClose={close}
        statusBarTranslucent
      >
        <View style={themeVars as any} className="flex-1">
          <View
            className="flex-1 items-center justify-center px-4"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <View
              className="w-full max-w-md bg-chalk rounded-2xl"
              style={{ height: "70%", maxHeight: 520 }}
            >
              <View className="flex-row justify-between items-center gap-3 px-5 pt-5 pb-3 border-b border-ash-3">
                <Text className="text-base text-onyx">
                  {t("LABEL_SELECT_COUNTRY")}
                </Text>
                <Pressable
                  onPress={close}
                  className="p-1.5 rounded-full hover:bg-ash-3"
                >
                  <CloseCircle
                    size={20}
                    color={themeVars["--color-ash-1"] as string}
                  />
                </Pressable>
              </View>

              <View className="px-5 pt-3 pb-2">
                <View className="flex-row items-center gap-2 bg-ash border border-ash-3 rounded-[10px] px-3 h-[44px]">
                  <SearchNormal1
                    size={18}
                    color={themeVars["--color-ash-2"] as string}
                  />
                  <TextInput
                    value={search}
                    onChangeText={setSearch}
                    placeholder={t("LABEL_SEARCH_COUNTRY")}
                    placeholderTextColor={themeVars["--color-ash-2"] as string}
                    className="flex-1 text-base text-onyx"
                    style={{ outlineStyle: "none" } as any}
                    autoFocus
                  />
                </View>
              </View>

              <FlatList
                data={filtered}
                keyExtractor={(item) => item.code}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator={false}
                renderItem={({ item }) => (
                  <Pressable
                    onPress={() => select(item.name)}
                    className="flex-row items-center gap-3 px-5 py-3 hover:bg-ash-3"
                  >
                    <Text className="text-xl">{codeToFlag(item.code)}</Text>
                    <Text className="text-base text-onyx flex-1">
                      {item.name}
                    </Text>
                  </Pressable>
                )}
                ItemSeparatorComponent={() => (
                  <View className="mx-5 border-b border-ash-1" />
                )}
                contentContainerStyle={{ paddingBottom: 8 }}
              />
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

export default CountryPicker;
